import { DateHelper } from "@/utility/dateHelper.js";
import { DEFAULT_LANG } from "@/utility/LocalizationService";
import { DateTime } from "luxon";

export const date = {
  methods: {
    getFriendlyFormatDateTime(lang = DEFAULT_LANG) {
      switch (lang) {
        case "it":
          return "d-m-Y H:i";
        case "en-US":
        default:
          return "Y-m-d H:i";
      }
    },
    getFriendlyFormatDateTimeExtended(lang = DEFAULT_LANG) {
      switch (lang) {
        case "it":
          return "M j Y, H:i";
        case "en-US":
        default:
          return "j M Y, H:i";
      }
    },
    getFormatDate(lang = DEFAULT_LANG) {
      switch (lang) {
        case "it":
          return "d-m-Y";
        case "en-US":
        default:
          return "Y-m-d";
      }
    },
    getLocalDateTimeNoSec(d, lang = DEFAULT_LANG, zone) {
      const dt = d ? new Date(d) : new Date();
      const formatLang = ["it", "en-US"].indexOf(lang) > -1 ? lang : DEFAULT_LANG;
      return this.formatDateTimeShort(dt, formatLang, zone);
    },
    getLocalDateTime(d, lang = DEFAULT_LANG, zone) {
      let res = "-";
      const formatLang = ["it", "en-US"].indexOf(lang) > -1 ? lang : DEFAULT_LANG;
      if (d) {
        const dt = d ? new Date(d) : new Date();
        if (dt instanceof Date && !isNaN(dt)) {
          res = this.formatDateTimeShortWithSeconds(dt, formatLang, zone);
        } else {
          console.error("Invalid date: " + d);
        }
      }
      return res;
    },
    getLocalDate(d, lang = DEFAULT_LANG) {
      const dt = d ? new Date(d) : new Date();
      const formatLang = ["it", "en-US"].indexOf(lang) > -1 ? lang : DEFAULT_LANG;

      return this.formatDateShort(dt, formatLang);
    },
    getLocalTime(d, zone) {
      try {
        if (d) {
          const dt = d ? new Date(d) : new Date();
          //return dt.toLocaleTimeString(lang);
          return this.getCustomFormatDate(dt, "HH:mm:ss", zone);
        } else {
          return "-";
        }
      } catch (e) {
        return "-";
      }
    },
    getCustomFormatDate(dt, format, zone) {
      return DateTime.fromJSDate(dt ? new Date(dt) : new Date(), zone ? { zone } : {}).toFormat(
        format
      );
    },
    // @return it => 05/10, en => 10/05
    getDayMonth(dt, lang = DEFAULT_LANG, zone) {
      return DateTime.fromJSDate(dt ? new Date(dt) : new Date(), zone ? { zone } : {})
        .setLocale(lang)
        .toLocaleString({ month: "numeric", day: "numeric" });
    },
    // @return it => 06/06/2020
    getDayMonthYear(dt, lang = DEFAULT_LANG, zone) {
      const f = { month: "2-digit", day: "2-digit", year: "numeric" };
      return dt
        ? DateTime.fromJSDate(dt ? new Date(dt) : new Date(), zone ? { zone } : {})
            .setLocale(lang)
            .toLocaleString(f)
        : "";
    },
    fancyTimeFormat(time) {
      return DateHelper.getFancyTimeFormat(time);
    },
    formatTimeWithoutSeconds(timestamp, lang = "en") {
      return DateHelper.formatTimeWithoutSeconds(timestamp, lang);
    },
    // @return it => 12 giu 2020 18:00
    formatDateMedFull(dt, lang = DEFAULT_LANG, zone) {
      return DateTime.fromJSDate(dt ? new Date(dt) : new Date(), zone ? { zone } : {})
        .setLocale(lang)
        .toLocaleString({
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "numeric"
        });
    },
    // @return it => 12 giu 2020 18:00:00
    formatDateMedFullWithSec(dt, lang = DEFAULT_LANG, zone) {
      return DateTime.fromJSDate(dt ? new Date(dt) : new Date(), zone ? { zone } : {})
        .setLocale(lang)
        .toLocaleString({
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "numeric",
          second: "numeric"
        });
    },
    // @return it => 12 giu 2020
    formatDateMed(dt, lang = DEFAULT_LANG, zone) {
      return DateTime.fromJSDate(dt ? new Date(dt) : new Date(), zone ? { zone } : {})
        .setLocale(lang)
        .toLocaleString({ month: "short", day: "numeric", year: "numeric" });
    },
    formatUtcDateMed(dt, lang = DEFAULT_LANG, zone) {
      return dt
        ? DateTime.fromISO(dt, { zone: zone ?? "utc" })
            .setLocale(lang)
            .toLocaleString({ month: "short", day: "numeric", year: "numeric" })
        : "";
    },
    // @return it => giu 2020
    formatDateMonthAndYear(dt, lang = DEFAULT_LANG, zone) {
      return DateTime.fromJSDate(dt ? new Date(dt) : new Date(), zone ? { zone } : {})
        .setLocale(lang)
        .toLocaleString({ month: "short", year: "numeric" });
    },
    // @return it => 12 giu
    formatDateMonthAndDay(dt, lang = DEFAULT_LANG, zone) {
      return DateTime.fromJSDate(dt ? new Date(dt) : new Date(), zone ? { zone } : {})
        .setLocale(lang)
        .toLocaleString({ month: "short", day: "numeric" });
    },
    // @return (from utc) it => 12 giu
    formatUtcDateMonthAndDay(dt, lang = DEFAULT_LANG, zone) {
      return dt
        ? DateTime.fromISO(dt, { zone: zone ?? "utc" })
            .setLocale(lang)
            .toLocaleString({ month: "short", day: "numeric" })
        : "";
    },
    // @return it => 06/06/2020 13:30:00
    formatDateTimeShortWithSeconds(dt, lang = "en", zone) {
      //let f = { month: 'short', day: 'numeric', year: 'numeric' };
      return DateTime.fromJSDate(dt ? new Date(dt) : new Date(), zone ? { zone } : {})
        .setLocale(lang)
        .toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS);
    },
    // @return it => 06/06/2020 13:30
    formatDateTimeShort(dt, lang = "en", zone) {
      //let f = { month: 'short', day: 'numeric', year: 'numeric' };
      return DateTime.fromJSDate(dt ? new Date(dt) : new Date(), zone ? { zone } : {})
        .setLocale(lang)
        .toLocaleString(DateTime.DATETIME_SHORT);
    },
    // @return it => 06/06/2020
    formatDateShort(dt, lang = "en", zone) {
      return DateTime.fromJSDate(dt ? new Date(dt) : new Date(), zone ? { zone } : {})
        .setLocale(lang)
        .toLocaleString({ year: "numeric", month: "numeric", day: "numeric" });
    },
    // @return it => 09:30
    formatTimeShort(dt, lang = "en", zone) {
      return DateTime.fromJSDate(dt ? new Date(dt) : new Date(), zone ? { zone } : {})
        .setLocale(lang)
        .toLocaleString(DateTime.TIME_24_SIMPLE);
    },
    // @return it => 09:30:00
    formatTimeShortWithSeconds(dt, lang = "en", zone) {
      return DateTime.fromJSDate(dt ? new Date(dt) : new Date(), zone ? { zone } : {})
        .setLocale(lang)
        .toLocaleString(DateTime.TIME_24_WITH_SECONDS);
    },
    // @return date to iso
    objDateToIso(timestamp, zone) {
      return DateTime.fromSeconds(timestamp, zone ? { zone } : {}).toISO();
    },
    // @return Today date with time at now
    objDateToday() {
      return DateTime.local().toJSDate();
    },
    // @return the date of "minusDays" value before today
    objDateTodayMinusDays(minusDays, setToStartOfDay = true) {
      return setToStartOfDay
        ? DateTime.local()
            .set({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 })
            .minus({ days: minusDays })
            .toJSDate()
        : DateTime.local().minus({ days: minusDays }).toJSDate();
    },
    // @return Today date with time at start of a period
    objDateTodayStartOf(startOfFormat = "day", dt, zone) {
      return DateTime.fromJSDate(dt ? new Date(dt) : new Date(), zone ? { zone } : {})
        .startOf(startOfFormat)
        .toJSDate();
    },
    // @return Today date with time at end of a period
    objDateTodayEndOf(endOfFormat = "day", dt, zone) {
      return DateTime.fromJSDate(dt ? new Date(dt) : new Date(), zone ? { zone } : {})
        .endOf(endOfFormat)
        .toJSDate();
    },
    // @return Current date plus a period of time
    objDatePlusTime(periodOfTime, dt, zone) {
      return DateTime.fromJSDate(dt ? new Date(dt) : new Date(), zone ? { zone } : {})
        .plus(periodOfTime)
        .toJSDate();
    },
    // @return Current date plus a period of time
    objDateMinusTime(periodOfTime, dt, zone) {
      return DateTime.fromJSDate(dt ? new Date(dt) : new Date(), zone ? { zone } : {})
        .minus(periodOfTime)
        .toJSDate();
    },
    // @return Days difference between 2 dates (from, to)
    diffInDays(from, to) {
      try {
        const end = DateTime.fromJSDate(new Date(to));
        const start = DateTime.fromJSDate(new Date(from));

        //var diffInMonths = end.diff(start, 'months');
        //diffInMonths.toObject(); //=> { months: 1 }
        const diff = end.diff(start, "days");

        return diff.values.days;
      } catch (e) {
        console.error(e);
        return false;
      }
    },
    // @return object with Hours, Minutes, Seconds with difference between 2 dates (from, to)
    diffHoursMinutesSeconds(from, to) {
      try {
        const end = DateTime.fromJSDate(new Date(to));
        const start = DateTime.fromJSDate(new Date(from));
        const diff = end.diff(start, ["hours", "minutes", "seconds"]);

        if (Math.round(diff.values.seconds) === 60) {
          diff.values.seconds = 0;
          diff.values.minutes += 1;
        }
        if (diff.values.minutes === 60) {
          diff.values.minutes = 0;
          diff.values.hours += 1;
        }

        return diff.values;
      } catch (e) {
        console.error(e);
        return false;
      }
    },
    diffHoursMinutesSecondsTostring(from, to) {
      const value = this.diffHoursMinutesSeconds(from, to);
      let str = "";
      str += value.hours > 0 ? Math.round(value.hours) + "h " : "";
      str += value.minutes > 0 || value.hours > 0 ? Math.round(value.minutes) + "m " : "";
      str += value.seconds >= 0 ? Math.round(value.seconds) + "s" : "";
      return str;
    },
    // @return milliseconds difference between 2 dates (from, to)
    diffTimes(from, to) {
      const end = DateTime.fromJSDate(new Date(to));
      const start = DateTime.fromJSDate(new Date(from));
      const diff = end.diff(start);
      return diff.values.milliseconds;
    },
    // @return seconds difference between 2 dates (from, to)
    diffSeconds(from, to) {
      return this.diffTimes(from, to) / 1000;
    },
    // @return it => 13:30:01
    formatTimeWithSeconds(timestamp, lang = "en") {
      return DateHelper.formatTimeWithSeconds(timestamp, lang);
    },
    // @return '1595336397075' unix millisecond timestamp
    formatDateUnixTimestamp(dt, zone) {
      return DateTime.fromJSDate(dt ? new Date(dt) : new Date(), zone ? { zone } : {}).toMillis();
    },
    fromMillisToTime(millis) {
      return DateTime.fromISO(DateTime.fromMillis(millis), {
        zone: "utc"
      }).toFormat("HH:mm:ss");
    },
    validationMax30Days() {
      const f = (start, end) => {
        const diff = Math.round(this.diffInDays(start, end));
        return diff > 30 ? false : true;
      };
      return { eval: f, errorMsg: `Max 30 ${this.$t("maintenance.unit.day")}` };
    },
    // @return 'yesterday', '2 days ago'
    relativeCalendar(dt, locale = DEFAULT_LANG) {
      return DateTime.fromJSDate(new Date(dt)).setLocale(locale).toRelativeCalendar();
    },
    // @return new date from object es. params: { year: 2020, month: 6, day: 6 }
    dateFromObj(dt, params) {
      return DateTime.fromJSDate(new Date(dt)).set(params).toJSDate();
    }
  }
};
